import { graphqlFetch } from '@api/graphql/gql-fetch';
import {
	type HasCurrentUserReviewedVendor,
	type ReviewsForVendor,
	hasCurrentUserReviewedVendorQuery,
	reviewsForVendorQuery,
} from '@api/graphql/reviews/reviewsForVendor';
import type { buildVariablesForReviewForVendors } from '@api/reviews/reviewsForVendor/build-variables';
import { mapReviewsForVendor } from '@api/reviews/reviewsForVendor/mapper';
import { getUnifiedReviewsUrl } from '@api/reviews/url';
import { XO_SESSION_TOKEN } from '@constants/membership';
import { Logger } from '@utils/logger/logger';
import { CookieStorage } from 'cookie-storage';

type Variables = Awaited<ReturnType<typeof buildVariablesForReviewForVendors>>;

export const fetchReviewsForVendors = async (
	storefrontId: string,
	variables: Variables,
) => {
	try {
		const result = await graphqlFetch<ReviewsForVendor>({
			url: getUnifiedReviewsUrl(),
			query: reviewsForVendorQuery,
			variables,
			headers: new Headers({ 'x-tenant-id': 'tk-us' }),
		});

		if (result.errors?.length) {
			const errors = result.errors.map((error) => new Error(error.message));
			for (const error of errors) {
				Logger.error({
					log_message: 'GraphQL Error fetching reviews for vendors',
					errorToLog: error,
					newRelic: true,
				});
			}
			throw errors;
		}

		return mapReviewsForVendor(
			storefrontId,
			result.data,
			variables.page,
			variables.size,
		);
	} catch (e) {
		if (!Array.isArray(e)) {
			Logger.error({
				errorToLog: e,
				newRelic: true,
				log_message: 'Error fetching reviews for vendors',
			});
		}
		throw e;
	}
};

export const fetchHasCurrentUserReviewedVendor = async (
	storefrontId: string,
	options?: { membershipSessionToken?: string | null },
): Promise<boolean> => {
	const membershipSessionToken =
		options?.membershipSessionToken !== undefined
			? options.membershipSessionToken
			: new CookieStorage().getItem(XO_SESSION_TOKEN);

	if (!membershipSessionToken) {
		return false;
	}

	const headers = new Headers({ 'x-tenant-id': 'tk-us' });
	headers.append('membership-session-token', membershipSessionToken);

	try {
		const result = await graphqlFetch<HasCurrentUserReviewedVendor>({
			url: getUnifiedReviewsUrl(),
			query: hasCurrentUserReviewedVendorQuery,
			variables: { id: storefrontId },
			headers,
		});

		if (result.errors?.length) {
			const errors = result.errors.map((error) => new Error(error.message));
			for (const error of errors) {
				Logger.error({
					log_message:
						'GraphQL Error fetching hasCurrentUserReviewedVendor for vendors',
					errorToLog: error,
					newRelic: true,
				});
			}
			throw errors;
		}

		return result.data?.hasCurrentUserReviewedVendor?.hasReviewed ?? false;
	} catch (e) {
		if (!Array.isArray(e)) {
			Logger.error({
				log_message: 'Error fetching hasCurrentUserReviewedVendor for vendors',
				errorToLog: e,
				newRelic: true,
			});
		}
		throw e;
	}
};
